// notebookWrites — how the web notebook talks to the server when it changes
// something (REQUIREMENTS_NOTEBOOK.md EN-1, EN-5, EN-6, BO-3, BO-5, NB-2, WE-1).
//
// Pure functions so they can be unit tested; Notebooks.tsx only wires them to
// the forms and the API.
import type { JournalEntry, Kind } from './notebookLayout';

// ── NB-2 (BOLUS_NOTES-79): times live in the PROFILE's timezone ────────────

function tzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/** The UTC instant of wall-clock `date` + `time` ('HH:MM') in timezone `tz`, as ISO. */
export function zonedToUtcIso(date: string, time: string, tz: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = (time || '12:00').split(':').map(Number);
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  const first = tzOffsetMs(wall, tz);
  let t = wall - first;
  const second = tzOffsetMs(t, tz);
  if (second !== first) t = wall - second;   // the guess crossed a DST change
  return new Date(t).toISOString();
}

/** Today's date and the current time ('HH:MM') in timezone `tz`. */
export function nowIn(tz: string, at: Date = new Date()): { date: string; time: string } {
  const date = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
  const time = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).format(at);
  return { date, time };
}

// ── EN-1 (BOLUS_NOTES-76): the client names new entries ─────────────────────

/** A lowercase UUID v4 for a new entry. Minted when the form opens, so a retried save reuses it. */
export function newClientId(): string {
  const c: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
  if (c?.randomUUID) return String(c.randomUUID()).toLowerCase();
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b); else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// ── building the calls ─────────────────────────────────────────────────────

export interface SaveContext {
  profileId: string;
  tz: string;          // the profile's timezone (journal.profile.timezone)
  date: string;        // the day the form is on, YYYY-MM-DD
  time: string;        // HH:MM from the form
  entry?: JournalEntry; // set when editing
  entryDate?: string;   // the day the edited entry was on when the form opened
  clientId: string;    // id for a new entry
}
export interface Call { action: string; payload: Record<string, any> }

/**
 * The server call for saving a form.
 * - New entries carry the client id (EN-1).
 * - Edits carry `if_updated_at` = the version the form was opened from (EN-5).
 * - An edit whose day and time were not touched sends no time, so the entry keeps
 *   its exact time (EN-6).
 * - A bolus edit only sends `mealId` when the form changed the link (BO-3);
 *   the bolus form adds `mealId` (null = unlink) or a nested `meal` (BO-5) itself.
 */
export function buildSave(kind: Kind, data: Record<string, any>, c: SaveContext): Call {
  const editing = !!c.entry;
  const unchangedTime = editing && c.entry!.local_time === c.time && (c.entryDate === undefined || c.entryDate === c.date);
  const p: Record<string, any> = { ...data, profileId: c.profileId, id: editing ? c.entry!.id : c.clientId };
  if (!unchangedTime) p.timestamp = zonedToUtcIso(c.date, c.time, c.tz);
  if (editing && c.entry!.updated_at) p.if_updated_at = c.entry!.updated_at;
  switch (kind) {
    case 'bolus': return { action: editing ? 'update_entry' : 'create_entry', payload: p };
    case 'basal': return { action: 'save_basal', payload: p };
    case 'meal': return { action: 'save_meal', payload: p };
    default: return { action: 'save_note', payload: { ...p, entry_date: c.date } };
  }
}

/** The server call for deleting an entry (EN-5: refused if it changed since it was shown). */
export function buildDelete(e: JournalEntry): Call {
  const action = e.kind === 'bolus' ? 'delete_entry' : e.kind === 'basal' ? 'delete_basal' : e.kind === 'meal' ? 'delete_meal' : 'delete_note';
  const payload: Record<string, any> = { id: e.id };
  if (e.updated_at) payload.if_updated_at = e.updated_at;
  return { action, payload };
}

// ── EN-7 / WE-1: failures in plain words ───────────────────────────────────

export interface SaveError { status: number; code: string; message: string; conflict: boolean; offline: boolean }

/** Turn an axios/Nexus failure into something to show. Nexus sends `{ code, message }` with message `CODE: text`. */
export function describeError(e: any): SaveError {
  const res = e?.response;
  if (!res) {
    const offline = e?.code === 'ERR_NETWORK' || /network/i.test(String(e?.message || '')) || (typeof navigator !== 'undefined' && navigator.onLine === false);
    return { status: 0, code: offline ? 'NETWORK' : 'ERROR', conflict: false, offline,
      message: offline ? 'No connection — nothing was saved. Try again when you are back online.' : String(e?.message || 'Something went wrong') };
  }
  const data = res.data || {};
  let message: string = typeof data === 'string' ? data : data.message || data.error || e.message || 'Something went wrong';
  let code: string = data.code || '';
  const m = /^([A-Z][A-Z_]{2,40}):\s*/.exec(message);
  if (m) { code = code && code !== 'FUNCTION_ERROR' ? code : m[1]; message = message.slice(m[0].length); }
  if (code === 'FORBIDDEN') message = 'You can view this notebook but not change it.';
  const conflict = res.status === 409 || code === 'CONFLICT';
  return { status: res.status, code, message, conflict, offline: false };
}
